import { cn } from '@/clientOnlyUtils/cn';
import React, { Children, type ComponentProps, cloneElement, isValidElement, useState } from 'react';

import { WaveSpinner } from '@/components/WaveSpinner';

import type { InputSize } from '~/components/Input/types';

import styles from './Table.module.scss';

type TableProps = Omit<ComponentProps<'div'>, 'size'> & {
  gridTemplateColumns: string;
  bordered?: boolean;
  size?: InputSize;
};

export const Table = ({ bordered, gridTemplateColumns, children, className, size }: TableProps) => {
  const [hoverId, setHoverId] = useState<React.Key | null>();

  const style = {
    gridTemplateColumns,
  };
  const classList = cn(
    styles.table,
    bordered ? styles.bordered : undefined,
    size ? styles[size] : undefined,
    className,
  );

  return (
    <div className={classList} style={style}>
      {Children.map(children, (child) => {
        if (isValidElement<CellProps>(child)) {
          return cloneElement(child, {
            hover: hoverId === child.key,
            onMouseEnter: () => setHoverId?.(child.key),
            onMouseLeave: () => setHoverId?.(null),
          });
        } else {
          return child;
        }
      })}
    </div>
  );
};

type CellProps = ComponentProps<'div'> & {
  index: number;
  active?: boolean;
  right?: boolean;
  hover?: boolean;
  test?: boolean;
};
export const Cell = ({ index, active, children, right, hover, onClick, className, ...props }: CellProps) => {
  const classList = cn(
    styles.cell,
    index === 0 ? styles.first : undefined,
    active ? styles.active : undefined,
    right ? styles.right : undefined,
    hover ? styles.hover : undefined,
    onClick != null ? styles.hoverable : undefined,
    className,
  );
  return (
    <div className={classList} onClick={onClick} {...props}>
      {children}
    </div>
  );
};

type CellHeaderProps = ComponentProps<'div'> & {
  right?: boolean;
};
export const CellHeader = ({ children, right, onClick, className }: CellHeaderProps) => {
  const classList = cn(
    styles.cellHeader,
    right ? styles.right : undefined,
    onClick != null ? styles.hoverable : undefined,
    className,
  );
  return (
    <div className={classList} onClick={onClick}>
      {children}
    </div>
  );
};

type CellLoadingProps = ComponentProps<'div'> & {
  index: number;
};
export const CellLoading = ({ children, index }: CellLoadingProps) => {
  const classList = cn(styles.cell, styles.cellLoading, index === 0 ? styles.first : undefined);
  return (
    <div className={classList}>
      <div className={styles.icon}>
        <WaveSpinner />
      </div>
      {children}
    </div>
  );
};
